/**
 * The queue's columns — Step 42.
 *
 * Kai: *"the columns are customisable & actual columns, can we do the same"*.
 * They were a hardcoded sequence of flex cells: the same seven for triage,
 * for review and for a backlog sweep, with no header row, so you could not
 * tell what a value WAS without inferring it from the shape.
 *
 * ── One registry, three consumers ──────────────────────────────────────
 *
 * The header, the cell and the chooser all read this list, so a column cannot
 * exist in one and not the others — the drift that `audit-automation-parity`
 * was written to catch, avoided here by construction rather than by a check.
 *
 * `width` is a fixed Tailwind class rather than content-sized on purpose: a
 * queue whose columns shift from row to row is unreadable, which is the
 * lesson the SLA slot already carries.
 */
import type { ReactNode } from 'react';
import { Badge } from '../../components/ui/badge';
import { cn } from '../../lib/utils';
import { fullTime, relativeTime } from '../../lib/time';
import { ticketRef } from '@itsm/shared';
import { SlaPill } from './SlaPill';
import { InlineCell, type InlineEditCtx } from './InlineCell';
import { AssignPicker } from './AssignPicker';

export interface QueueRow {
  id: string;
  number: number;
  type: string;
  subject: string;
  status: string | null;
  priority: string;
  requester: { name: string | null; email: string };
  assigneeId?: string | null;
  assigneeName?: string | null;
  groupId?: string | null;
  groupName?: string | null;
  source?: string | null;
  sla?: unknown;
  createdAt: number;
  updatedAt: number;
}

export interface ColumnCtx {
  statusName: (key: string) => string;
  statusClass: (key: string) => string;
  sourceLabel: (key: string | null | undefined) => string;
  onOpen: (id: string) => void;
  /** Inline editing, or `null` when this agent may not write tickets.
   *  `null` rather than optional so a caller that forgets it gets a type
   *  error instead of silently handing everyone an editable queue — the
   *  fail-closed rule, applied to a prop. */
  inline: InlineEditCtx | null;
}

export interface QueueColumn {
  id: string;
  label: string;
  /**
   * Default width in PIXELS, not a Tailwind class.
   *
   * Two reasons, both from Kai: *"I should be able to drag the columns to
   * make them bigger and smaller, also the text should never overlap like it
   * is now."* A class cannot be dragged, and a row of fixed-width flex cells
   * has no mechanism preventing one cell's content from running into the
   * next. A grid built from numbers can be resized, and its tracks cannot
   * overlap by construction.
   */
  width: number;
  /** The one column that absorbs slack — rendered as `minmax(px, 1fr)`. */
  flex?: boolean;
  /** Right-aligned numerics and dates read better as a block. */
  align?: 'right';
  /**
   * There is deliberately NO responsive-hide option.
   *
   * There was one — `hideBelow: 'md' | 'lg'` — and it was broken twice over.
   * A `display:none` grid item does not hold its track; it vacates it, and
   * every later cell shifts left into a track sized for a different column.
   * Measured in Chrome with four of nine hidden: SLA rendered at the
   * assignee track's 128px instead of its own 144, Updated at 128 instead of
   * 104, and 456px of empty track was left scrollable off the right-hand
   * side. Header and rows shifted together, so it read as "the widths are
   * wrong" rather than as a bug with a cause.
   *
   * It was also answering a question that no longer exists: the table gained
   * a real horizontal scroll container, so a narrow window scrolls. Hiding a
   * column the user explicitly chose in order to avoid a scrollbar overrides
   * their choice to save them from the thing that already works.
   */
  cell: (t: QueueRow, ctx: ColumnCtx) => ReactNode;
}

/** Drag bounds. Below the floor a header label is unreadable; above the
 *  ceiling one column pushes every other off the screen. */
export const COLUMN_MIN = 64;
export const COLUMN_MAX = 560;
/** The checkbox + priority-dot gutter, shared by the header and the rows so
 *  they cannot drift by a pixel. */
export const GUTTER_PX = 28;

/**
 * The column rules — the header's drag handle and the body's cell dividers.
 *
 * Named here rather than written twice, because the two MUST land on the same
 * x and read as one line: the handle is only discoverable if the rule below it
 * points at it.
 *
 * Both derive from `--muted-foreground`, NOT `--border`. Kai: *"they probably
 * also need to be more obvious lines, especially on dark mode."* `--border` is
 * the token for a panel EDGE, where the panel's own fill supplies the contrast;
 * a free-standing rule inside a surface has no fill behind it and so needs a
 * colour that carries on its own. Dark mode gets the larger alpha because
 * `--border` there (17.5% L) sits only ~12 points off the background (4.9% L),
 * and near-black differences are the first thing ambient glare eats.
 *
 * Dimming — `dark:bg-border/90` — is the trap: it reads as "adjust for dark"
 * while making the line strictly fainter than the light-mode one.
 */
/**
 * The grid's gap and the table's side padding, in pixels.
 *
 * Numbers rather than `gap-3 px-4`, because `minTableWidth` has to add them up
 * and a Tailwind class cannot be read back. They were classes, the maths
 * ignored them, and the table overflowed its own background — so both the
 * layout and the arithmetic now read these.
 */
export const COLUMN_GAP_PX = 12;
export const TABLE_PAD_PX = 32;

export const COLUMN_RULE = 'bg-muted-foreground/25 dark:bg-muted-foreground/40';
export const COLUMN_DIVIDER = 'border-muted-foreground/15 dark:border-muted-foreground/25';

export const QUEUE_COLUMNS: QueueColumn[] = [
  {
    id: 'ref',
    label: 'Ref',
    width: 96,
    cell: (t, ctx) => (
      <button
        onClick={(e) => {
          e.stopPropagation();
          ctx.onOpen(t.id);
        }}
        className="text-left text-xs tabular-nums text-muted-foreground hover:text-brand hover:underline"
      >
        {ticketRef(t.type, t.number)}
      </button>
    ),
  },
  {
    id: 'subject',
    label: 'Subject',
    // The one column that takes the slack: everything else is a fixed track,
    // so the grid stays aligned while the subject absorbs the window.
    width: 320,
    flex: true,
    cell: (t) => (
      <>
        <span className="block truncate text-sm font-medium">{t.subject}</span>
        <span className="block truncate text-xs text-muted-foreground">
          {t.requester.name ?? t.requester.email}
        </span>
      </>
    ),
  },
  {
    id: 'status',
    label: 'Status',
    width: 112,
    cell: (t, ctx) => {
      const badge = t.status ? (
        <Badge variant="outline" className={cn('border-transparent', ctx.statusClass(t.status))}>
          {ctx.statusName(t.status)}
        </Badge>
      ) : (
        <span className="text-muted-foreground">—</span>
      );
      if (!ctx.inline) return t.status ? badge : null;
      return (
        <InlineCell
          value={t.status ?? ''}
          options={ctx.inline.statuses}
          display={badge}
          placeholder="No status"
          // The status KEY, which is what the PATCH takes — the id is per-org
          // and the queue never sees it.
          onCommit={(next) => ctx.inline!.commit(t.id, { status: next })}
        />
      );
    },
  },
  {
    /**
     * Group and agent in ONE control. Kai: *"agent and group should be
     * editable together in one column"*, then *"the UI is still not great —
     * replicate the capture"*. See `AssignPicker` for the measurements taken
     * off `Reassign.html`.
     */
    id: 'assignment',
    // The reference's own label for this column, and the right one: it names
    // the DECISION rather than the two fields the decision happens to touch.
    label: 'Assigned to',
    width: 200,
    cell: (t, ctx) => (
      <AssignPicker
        groupId={t.groupId}
        groupName={t.groupName}
        assigneeId={t.assigneeId}
        assigneeName={t.assigneeName}
        groups={ctx.inline?.groups ?? []}
        agentsFor={ctx.inline?.agentsFor ?? (() => [])}
        onCommit={(patch) => ctx.inline!.commit(t.id, patch)}
        // Fails closed: without `tickets.edit` there is no inline context and
        // the cell is a read-only line of text.
        readOnly={!ctx.inline}
      />
    ),
  },
  {
    id: 'source',
    label: 'Source',
    width: 104,
    align: 'right',
    cell: (t, ctx) => <span className="truncate">{ctx.sourceLabel(t.source)}</span>,
  },
  {
    id: 'sla',
    label: 'SLA',
    width: 144,
    align: 'right',
    // Always rendered, even when empty: sizing this to its content shifted
    // every column on rows that had a clock.
    cell: (t) => (t.sla ? <SlaPill clock={t.sla as never} /> : null),
  },
  {
    id: 'updated',
    label: 'Updated',
    width: 104,
    align: 'right',
    cell: (t) => (
      <span className="tabular-nums" title={fullTime(t.updatedAt)}>
        {relativeTime(t.updatedAt)}
      </span>
    ),
  },
  {
    id: 'created',
    label: 'Created',
    width: 104,
    align: 'right',
    cell: (t) => (
      <span className="tabular-nums" title={fullTime(t.createdAt)}>
        {relativeTime(t.createdAt)}
      </span>
    ),
  },
];

/** Shown when a view says nothing. Deliberately not everything: a default of
 *  every column is a wall nobody reads. */
export const DEFAULT_COLUMNS = ['ref', 'subject', 'status', 'assignment', 'sla', 'updated'];

/**
 * `subject` is not removable.
 *
 * Without it a row is a set of codes with nothing to identify the ticket, and
 * the chooser would be offering people a way to make the queue useless.
 */
export const MANDATORY_COLUMNS = new Set(['subject']);

/**
 * Columns that USED to exist, and what they became.
 *
 * Saved views and shared links carry `cols=…,assignee,group,…` and would
 * otherwise quietly lose those columns — `resolveColumns` drops ids it does
 * not recognise, which is right for a deleted column and wrong for a renamed
 * one. A view holding both maps to one `assignment`, deduped below.
 */
const COLUMN_ALIASES: Record<string, string> = { assignee: 'assignment', group: 'assignment' };

/**
 * Stored ids → live ids, deduped and order-preserving.
 *
 * Exported because the CHOOSER needs the same answer as the table. It filters
 * the stored list against the registry, so without this a view naming
 * `assignee` would render the merged column while the chooser showed it
 * unticked — and saving from that chooser would then drop it for real.
 */
export function normaliseColumnIds(ids: string[]): string[] {
  return [...new Set(ids.map((id) => COLUMN_ALIASES[id] ?? id))];
}

export function resolveColumns(ids: string[] | null | undefined): QueueColumn[] {
  const wanted = normaliseColumnIds(ids?.length ? ids : DEFAULT_COLUMNS);
  // Ordered by the STORED list, so a view's column order is its own — and
  // unknown ids (a column removed from the code) are dropped rather than
  // crashing a saved view.
  const cols = wanted.map((id) => QUEUE_COLUMNS.find((c) => c.id === id)).filter(Boolean) as QueueColumn[];
  if (!cols.some((c) => c.id === 'subject')) {
    const subject = QUEUE_COLUMNS.find((c) => c.id === 'subject')!;
    return [subject, ...cols];
  }
  return cols;
}

/**
 * Parse the width overrides carried in the URL (`ref:80,subject:400`).
 *
 * Overrides only, not the whole set: a view that has never been resized
 * stores nothing, and a column added to the code later gets its default
 * rather than vanishing behind a stale saved width.
 */
export function parseWidths(raw: string | null | undefined): Record<string, number> {
  const out: Record<string, number> = {};
  for (const part of (raw ?? '').split(',')) {
    const [id, px] = part.split(':');
    const n = Number(px);
    if (!id || !Number.isFinite(n)) continue;
    out[id] = Math.min(COLUMN_MAX, Math.max(COLUMN_MIN, Math.round(n)));
  }
  return out;
}

export function serialiseWidths(widths: Record<string, number>): string | null {
  const entries = Object.entries(widths);
  return entries.length ? entries.map(([id, px]) => `${id}:${px}`).join(',') : null;
}

/**
 * The grid template. The flexible column becomes `minmax(px, 1fr)` so it
 * absorbs slack but never collapses below legibility; everything else is a
 * fixed track, which is what stops cells overlapping.
 */
export function gridTemplate(cols: QueueColumn[], widths: Record<string, number>): string {
  return cols
    .map((c) => {
      const px = widths[c.id] ?? c.width;
      return c.flex ? `minmax(${px}px, 1fr)` : `${px}px`;
    })
    .join(' ');
}

/**
 * Total width the table cannot go below, so it scrolls sideways rather than
 * squeezing tracks into each other.
 *
 * **The tracks are not the whole width.** This counted only the columns at
 * first, which under-reported the real minimum by the gaps plus the padding —
 * 140px with every column showing. The grid then overflowed its own border
 * box, because fixed tracks cannot shrink and the flexible one has a floor,
 * and everything painted ON that box stopped short of the content: the
 * header's tint and bottom border, each row's rule, and the hover highlight
 * all ended a column and a half early. Kai: *"when all the columns are
 * selected, there's some bad UI."*
 *
 * The gap and the padding therefore come from `COLUMN_GAP_PX` /
 * `TABLE_PAD_PX`, which the header and rows also *render* from — one number
 * each, so the arithmetic here cannot drift from the layout it describes.
 */
export function minTableWidth(cols: QueueColumn[], widths: Record<string, number>): number {
  // The gutter holds a 14px checkbox, a 6px gap and an 8px dot.
  const tracks = cols.reduce((n, c) => n + (widths[c.id] ?? c.width), GUTTER_PX);
  // One gap BETWEEN each pair of tracks — the gutter is a track too, so with
  // n columns there are n gaps, not n - 1.
  return tracks + cols.length * COLUMN_GAP_PX + TABLE_PAD_PX;
}
